import { BrowserRouter, Routes, Route, Navigate, Outlet, NavLink } from 'react-router-dom';
import { useAuth } from './providers/AuthProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppLogo } from './components/AppLogo';
import { OfflineIndicator } from './components/OfflineIndicator';
import { Dashboard } from './pages/Dashboard';
import { Trades } from './pages/Trades';
import { TradeDetail } from './pages/TradeDetail';
import { Reports } from './pages/Reports';
import { Plans } from './pages/Plans';
import { Settings } from './components/Settings';
import { Login } from './pages/Login';
import { NotFound } from './pages/NotFound';
import { Changelog } from './pages/Changelog';
import { OnboardingGate } from './components/onboarding/OnboardingGate';
import { VersionFooter } from './components/VersionFooter';
import { useHasUnseenVersions } from './hooks/useHasUnseenVersions';
import { getRuntimeConfig, DEFAULT_MARKETING_URL } from './services/runtime-config';

/**
 * Protected route wrapper — redirects to login if not authenticated.
 * Validates: Requirements 12.4, 15.5
 */
function ProtectedRoute() {
  const { isAuthenticated, isLoading, logout } = useAuth();
  // Call unconditionally at the top, before any early returns, to satisfy the
  // Rules of Hooks (Req 6.2, 6.5, 6.6).
  const hasUnseen = useHasUnseenVersions();
  const marketingUrl = getRuntimeConfig()?.marketingUrl ?? DEFAULT_MARKETING_URL;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <OnboardingGate>
    <div className="app-layout">
      <header className="app-header">
        <nav aria-label="Main navigation" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          {/* Brand: icon + title + strapline (shared across all pages) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <a
              href={marketingUrl}
              aria-label="Covered Calls — go to the marketing site"
              style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <AppLogo size={36} />
            </a>
            <div>
              <strong style={{ fontSize: '1.1rem', display: 'block', lineHeight: 1.2 }}>Covered Calls Tracker</strong>
              <span style={{ fontSize: '0.72rem', color: 'var(--color-header-muted)' }}>
                Generate consistent income from your stock portfolio through covered call strategies
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
            <NavLink to="/" style={({ isActive }) => ({ color: isActive ? 'var(--color-header-text)' : 'var(--color-header-muted)', textDecoration: 'none', fontWeight: isActive ? 600 : 400 })}>Dashboard</NavLink>
            <NavLink to="/trades" style={({ isActive }) => ({ color: isActive ? 'var(--color-header-text)' : 'var(--color-header-muted)', textDecoration: 'none', fontWeight: isActive ? 600 : 400 })}>Trades</NavLink>
            <NavLink to="/reports" style={({ isActive }) => ({ color: isActive ? 'var(--color-header-text)' : 'var(--color-header-muted)', textDecoration: 'none', fontWeight: isActive ? 600 : 400 })}>Reports</NavLink>
            <NavLink to="/settings" style={({ isActive }) => ({ color: isActive ? 'var(--color-header-text)' : 'var(--color-header-muted)', textDecoration: 'none', fontWeight: isActive ? 600 : 400 })}>Settings</NavLink>
            <NavLink to="/changelog" style={({ isActive }) => ({ color: isActive ? 'var(--color-header-text)' : 'var(--color-header-muted)', textDecoration: 'none', fontWeight: isActive ? 600 : 400 })}>
              What&apos;s New
              {hasUnseen ? (
                <span className="whats-new-badge" aria-label="New changes available">
                  <span className="sr-only">New changes available</span>
                </span>
              ) : null}
            </NavLink>
            <button
              onClick={() => logout()}
              style={{ background: 'none', border: '1px solid var(--color-header-muted)', color: 'var(--color-header-muted)', padding: '0.25rem 0.75rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', minHeight: '32px' }}
            >
              Sign Out
            </button>
          </div>
        </nav>
      </header>
      <main className="app-main">
        <Outlet />
      </main>
      <VersionFooter />
    </div>
    </OnboardingGate>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <OfflineIndicator />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/trades" element={<Trades />} />
            <Route path="/trades/:id" element={<TradeDetail />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/changelog" element={<Changelog />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
